/**
 * Structured logging utility with log levels
 * 
 * Refactored to a functional factory to avoid class-based TDZ issues in production bundles.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

export interface Logger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  setLevel(level: LogLevel): void;
  child(prefix: string): Logger;
}

/**
 * Factory for creating module-specific loggers
 */
export function createLogger(module: string): Logger {
  let currentLevel = (typeof import.meta !== 'undefined' && import.meta.env?.DEV)
    ? LogLevel.DEBUG
    : LogLevel.WARN;

  const formatMessage = (message: string): string => {
    return module ? `[${module}] ${message}` : message;
  };

  return {
    debug(message: string, ...args: unknown[]): void {
      if (currentLevel <= LogLevel.DEBUG) {
        console.log(formatMessage(message), ...args);
      }
    },
    info(message: string, ...args: unknown[]): void {
      if (currentLevel <= LogLevel.INFO) {
        console.info(formatMessage(message), ...args);
      }
    },
    warn(message: string, ...args: unknown[]): void {
      if (currentLevel <= LogLevel.WARN) {
        console.warn(formatMessage(message), ...args);
      }
    },
    error(message: string, ...args: unknown[]): void {
      if (currentLevel <= LogLevel.ERROR) {
        console.error(formatMessage(message), ...args);
      }
    },
    setLevel(level: LogLevel): void {
      currentLevel = level;
    },
    child(prefix: string): Logger {
      const childLogger = createLogger(module ? `${module}:${prefix}` : prefix);
      childLogger.setLevel(currentLevel);
      return childLogger;
    }
  };
}
